import { deduplicateAsync } from '@common/core/utils/deduplicateAsync'

import { getGateways as _getGateways, removeGateway as _removeGateway } from '../gateways'
import type { GatewayTimeFilterInput } from '../types/gateway-filter.type'
import { resolveGatewayTimeFilter } from '../utils/resolveGatewayTimeFilter'

import { useGatewaysStore } from './gateways.store'

const GATEWAYS_FETCH_TIMEOUT_MS = 15000
let latestGatewaysRequestId = 0

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs)
      }),
    ])
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }
  }
}

function buildGetGatewaysRequestKey(projectId: string, filter?: GatewayTimeFilterInput): string {
  const resolved = resolveGatewayTimeFilter(filter)
  return `${projectId}:${resolved.dateFrom}:${resolved.dateTo}`
}

const fetchGatewaysByRequestKey = deduplicateAsync(async (requestKey: string): Promise<void> => {
  const [projectId, dateFromRaw, dateToRaw] = requestKey.split(':')
  if (!projectId) {
    return
  }

  const dateFrom = dateFromRaw ?? ''
  const dateTo = dateToRaw ?? ''
  const filter = dateFrom.length > 0 && dateTo.length > 0 ? { dateFrom, dateTo } : undefined

  const requestId = ++latestGatewaysRequestId
  const { setState } = useGatewaysStore.getState()
  setState({ loading: true, error: null })
  try {
    const data = await withTimeout(
      _getGateways(projectId, filter),
      GATEWAYS_FETCH_TIMEOUT_MS,
      'Timed out while loading gateways'
    )

    if (requestId !== latestGatewaysRequestId) {
      return
    }

    setState({ loading: false, gateways: data, lastFetchedProjectId: projectId })
  } catch (err) {
    if (requestId !== latestGatewaysRequestId) {
      return
    }

    setState({
      gateways: [],
      lastFetchedProjectId: null,
      error: err instanceof Error ? err.message : 'Failed to load gateways',
    })
    throw err
  } finally {
    if (requestId === latestGatewaysRequestId) {
      setState({ loading: false })
    }
  }
})

export async function getGateways(projectId: string, filter?: GatewayTimeFilterInput): Promise<void> {
  return fetchGatewaysByRequestKey(buildGetGatewaysRequestKey(projectId, filter))
}

export function resetGatewaysState(): void {
  latestGatewaysRequestId += 1
  useGatewaysStore.getState().setState({ loading: false, error: null, gateways: [], lastFetchedProjectId: null })
}

export async function removeGateway(
  gatewayId: string,
  projectId: string,
  filter?: GatewayTimeFilterInput
): Promise<void> {
  await _removeGateway(gatewayId)
  await getGateways(projectId, filter)
}
